























































































































































































import { Vue, Component, Prop, Watch, Ref } from '$/lib/vueExt';
import { formSelectOptions }                from '$/lib/utils';
import ConfirmationModal                    from '$/lib/widgets/ConfirmationModal.vue';
import { showProgress }                     from '$/lib/widgets/ProgressModal.vue';
import type BForm                           from '$/lib/widgets/Form.vue';

import { Lease } from '$/entities/Lease';
import { Building, BuildingDoc, BuildingStatus, BuildingUnit } from '$/entities/Building';

import BuildingUnitInput from '$/screens/App/Buildings/components/BuildingUnit.vue';

@Component({
	components : {
		BuildingUnitInput,
	},
})
export default class BuildingModal extends Vue {

	@Prop()
	readonly building: Building;

	@Ref()
	readonly buildingForm: BForm;

	@Ref()
	readonly buildingInUseConfirmation: ConfirmationModal;

	isVisible           = false;
	leasesCount         = 0;	// the number of leases for this.building
	isEditingDocs       = false;
	docs: BuildingDoc[] = [];
	documentsError      = '';

	newUnit: BuildingUnit = this.getNewUnit();

	get buildingStatuses() {
		return formSelectOptions(BuildingStatus, { invert : true });
	}

	get modalTitle() {
		if (this.building.externalId) {
			return this.building.getName();
		}
		return this.building.isNew ? 'Add a Current or Former Property' : 'Edit Property';
	}

	get tools() {
		return {
			'Generate Random Values' : !this.building.externalId ? () => Building.generateAtRandom({ building : this.building }) : undefined,
		};
	}

	/**
	 * @returns the largest unit number + 1 or 1 if there are no units with a number
	 */
	get newUnitNumber() {
		return (_.max((this.building?.units ?? []).map(unit => Number(unit.number)).filter(number => !isNaN(number))) || 0) + 1;
	}

	@Watch('building')
	async onBuildingChange(building: Building) {
		this.isVisible     = !!building;
		this.newUnit       = this.getNewUnit();
		this.isEditingDocs = false;
		void this.onIsEditingDocsChange();	// force loading of docs
		this.building?.makeEditable({ force : true });
	}

	@Watch('isEditingDocs')
	async onIsEditingDocsChange() {
		this.docs = await this.building?.getBuildingDocs() ?? [];
		this.docs.forEach(doc => doc.file?.makeEditable());
	}

	@Watch('building.address.street')
	async onStreetChange() {
		// moves any unit numbers from the street to the units table
		await this.building?.autofixValidationErrors('address');
	}

	/**
	 * Sort units manually to prevent immediate reaction to changes made to the rendered units
	 */
	sortUnits() {
		this.building.units = this.building.unitsSorted;
	}

	/**
	 * Returns a new unit
	 */
	getNewUnit() {
		return new BuildingUnit({
			number   : `${this.newUnitNumber}`,
			type     : null,
			bedrooms : null,
		});
	}

	addUnit(unit: BuildingUnit) {
		unit ??= this.newUnit;
		if (!unit) {
			return;
		}

		this.building.units.push(unit);
		if (unit === this.newUnit) {
			this.newUnit = this.getNewUnit();
		}
		this.sortUnits();

		this.building.unitsClaimedToOwn = Math.max(this.building.unitsClaimedToOwn, this.building.units.length);
	}

	removeUnit(unit: BuildingUnit) {
		this.building.units = this.building.units.filter(existingUnit => existingUnit.number !== unit.number);
	}

	async fillList() {
		// Adding each unit requires a small delay, so there's a limit to how many it should try to create at once so it doesn't take forever
		const numUnitsToAdd = Math.min(this.building.unitsClaimedToOwn - this.building.units.length, 10);
		for (let i = 0; i < numUnitsToAdd; i++) {
			const unit = this.getNewUnit();
			this.addUnit(unit);
			await _.delay(50);
			(this.$refs[`unit-${unit.number}`][0] as BuildingUnitInput).editUnit();
		}
	}

	async saveBuilding() {
		this.building.org ??= this.$org;

		if (await this.buildingForm.isNotValid()) {
			return;
		}

		const buildingValidationErrors = await this.building.getValidationErrors({ autofix : true });
		if (buildingValidationErrors?.address || await this.building.isNotValid()) {
			if (typeof buildingValidationErrors?.address?.[0] === 'string') {
				this.showAlert(buildingValidationErrors.address[0].toString());
			}
			return;
		}

		await this.building.save();

		this.documentsError = await showProgress(
			progress => this.building.uploadBuildingDocs(_.map(this.docs, 'file'), progress),
			{ message : 'Uploading building documents...' }
		);
		if (!this.documentsError) {
			this.hide();
		}
	}

	async archiveBuilding() {
		this.leasesCount = await Lease.count({ where : { building : this.building } });
		if (this.leasesCount && !(await this.buildingInUseConfirmation.showConfirm())) {
			return;
		}

		await this.building.archive();
		this.hide();
	}

	async onBeforeHide(event) {
		if (this.isVisible && this.building.isEdited() || this.docs.some(doc => doc.file.isEdited())) {
			event.preventDefault();
			if (await this.showConfirmUnsavedChanges()) {
				this.hide();
			}
		}
		else if (this.isVisible && this.building.isNew) {
			this.hide();
		}
	}

	hide() {
		this.isVisible = false;
		this.building.resetEdited();
		this.docs = [];
		this.$emit('finished');
	}

}
